import type { TagAttrs } from '@/types';

// ─── Time Formatters ──────────────────────────────────────────────────────────

/** Convert seconds to mm:ss display string */
export function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Calculate scrub bar percentage */
export function secondsToPercent(current: number, total: number): number {
  if (total === 0) return 0;
  return Math.min((current / total) * 100, 100);
}

/** Convert ms to a "m:ss" display string (no leading zero on minutes) */
export function msToTimestamp(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Parse a "m:ss" or bare-seconds timestamp input into milliseconds */
export function parseTimestampInput(val: string): number | null {
  const trimmed = val.trim();
  if (!trimmed) return null;
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    return (parseInt(colonMatch[1], 10) * 60 + parseInt(colonMatch[2], 10)) * 1000;
  }
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : Math.round(num * 1000);
}

// ─── String Helpers ───────────────────────────────────────────────────────────

/**
 * Some backend endpoints return all_sections entries (scenes, activities,
 * key_points, etc.) as plain strings; others return { text, timestamp_ms }
 * objects. Coerce either shape to plain text so callers never hand React
 * a raw object as a child (which throws at render time).
 */
export function toDisplayText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'text' in value) {
    return String((value as { text: unknown }).text ?? '');
  }
  return value == null ? '' : String(value);
}

// Keys whose value reads naturally on its own, without a "key: " prefix.
const ATTRS_UNLABELED_KEYS = new Set(['appearance', 'clothing', 'label']);
// Friendlier labels for keys that read awkwardly verbatim (image attrs use camelCase).
const ATTRS_KEY_LABELS: Record<string, string> = {
  visible_text: 'text',
  brandOrLogoText: 'text',
  framePosition: 'position',
  names: 'name',
};

/**
 * Renders a person/object tag's TagAttrs as a single tooltip string, or null if
 * nothing was detected. Video attrs are a fixed 4-key set (appearance/clothing/
 * colors/visible_text); image attrs carry a larger, different key set — this
 * iterates whatever keys are present rather than assuming either shape, and
 * skips null/empty values. Shared by TranscriptPanel and AssetDetailDrawer so
 * both surfaces format attrs the same way.
 */
export function formatAttrsTooltip(attrs: TagAttrs | null | undefined): string | null {
  if (!attrs) return null;
  const parts = Object.entries(attrs)
    .filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([key, v]) => {
      const value = Array.isArray(v) ? v.join(', ') : String(v);
      if (ATTRS_UNLABELED_KEYS.has(key)) return value;
      return `${ATTRS_KEY_LABELS[key] ?? key}: ${value}`;
    });
  return parts.length ? parts.join(' · ') : null;
}

/** Safely escape HTML for use in dangerouslySetInnerHTML contexts */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Truncate a string to maxLength with ellipsis */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1) + '…';
}

// ─── Number Formatters ────────────────────────────────────────────────────────

/** Format large numbers: 1200 → 1.2K, 1200000 → 1.2M */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
